import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Clock, Plus, ArrowLeft, User, Briefcase, FileText, Trash2, Settings, Edit, Calendar as CalendarIcon, Download, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import HolidayCalendar from '../../components/HolidayCalendar';

const ApplyLeaveModal = ({ show, onClose, onSuccess, balance, holidays = [] }) => {
    const [formData, setFormData] = useState({
        type: 'CASUAL',
        startDate: '',
        endDate: '',
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await api.post('/leaves/apply', formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to apply');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Apply for Leave</h2>
                {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label htmlFor="leave-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Leave Type</label>
                        <select
                            id="leave-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        >
                            <option value="CASUAL">Casual Leave ({balance?.casual || 0} remaining)</option>
                            <option value="SICK">Sick Leave ({balance?.sick || 0} remaining)</option>
                            <option value="EARNED">Earned Leave ({balance?.earned || 0} remaining)</option>
                            <option value="UNPAID">Unpaid Leave</option>
                        </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label htmlFor="start-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Start Date</label>
                            <input
                                id="start-date"
                                type="date"
                                required
                                value={formData.startDate}
                                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="end-date" style={{ display: 'block', marginBottom: '0.5rem' }}>End Date</label>
                            <input
                                id="end-date"
                                type="date"
                                required
                                value={formData.endDate}
                                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                            />
                        </div>
                    </div>

                    {/* Leave Duration & Effective Days Calculation */}
                    {formData.startDate && formData.endDate && (
                        (() => {
                            const start = new Date(formData.startDate);
                            const end = new Date(formData.endDate);

                            // Validate dates
                            if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
                                return null;
                            }

                            let totalDays = 0;
                            let weekendCount = 0;
                            let holidayCount = 0;
                            const holidayDetails = [];

                            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                totalDays++;
                                const dayOfWeek = d.getDay(); // 0 = Sun, 6 = Sat
                                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                const holiday = holidays.find(h => {
                                    const hDate = new Date(h.date);
                                    return h.isActive && hDate.toDateString() === d.toDateString();
                                });

                                if (holiday) {
                                    holidayCount++;
                                    holidayDetails.push(holiday);
                                } else if (isWeekend) {
                                    weekendCount++;
                                }
                            }

                            const effectiveDays = totalDays - holidayCount - weekendCount;

                            return (
                                <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Duration:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right' }}>{totalDays} days</div>

                                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Holidays:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right', color: '#d97706' }}>- {holidayCount}</div>

                                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Weekends:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right', color: '#6b7280' }}>- {weekendCount}</div>

                                        <div style={{ borderTop: '1px solid #e5e7eb', gridColumn: '1 / -1', margin: '0.25rem 0' }}></div>

                                        <div style={{ fontWeight: '600', color: '#111827' }}>Effective Leave:</div>
                                        <div style={{ fontWeight: 'bold', textAlign: 'right', color: '#2563eb' }}>{effectiveDays} days</div>
                                    </div>

                                    {holidayDetails.length > 0 && (
                                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.25rem', fontSize: '0.85rem', color: '#92400e' }}>
                                            <strong>Included Holidays:</strong>
                                            <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                                                {holidayDetails.map((h, i) => (
                                                    <li key={i}>{new Date(h.date).toLocaleDateString()}: {h.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}

                    <div>
                        <label htmlFor="leave-reason" style={{ display: 'block', marginBottom: '0.5rem' }}>Reason</label>
                        <textarea
                            id="leave-reason"
                            required
                            rows="3"
                            value={formData.reason}
                            onChange={e => setFormData({ ...formData, reason: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        ></textarea>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Submitting...' : 'Apply'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Edit Leave Modal
const EditLeaveModal = ({ show, onClose, onSuccess, leave, holidays = [] }) => {
    const [formData, setFormData] = useState({
        type: 'CASUAL',
        startDate: '',
        endDate: '',
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (leave) {
            setFormData({
                type: leave.type,
                startDate: new Date(leave.startDate).toISOString().split('T')[0],
                endDate: new Date(leave.endDate).toISOString().split('T')[0],
                reason: leave.reason || ''
            });
        }
    }, [leave]);

    if (!show || !leave) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await api.put(`/leaves/${leave.id}`, formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Edit Leave Request</h2>
                {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Reuse similar form fields as ApplyModal - skipping full duplication for brevity in this replace block, but in real scenario would modularize form fields */}
                    <div>
                        <label htmlFor="edit-leave-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Leave Type</label>
                        <select
                            id="edit-leave-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        >
                            <option value="CASUAL">Casual Leave</option>
                            <option value="SICK">Sick Leave</option>
                            <option value="EARNED">Earned Leave</option>
                            <option value="UNPAID">Unpaid Leave</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label htmlFor="edit-start-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Start Date</label>
                            <input
                                id="edit-start-date"
                                type="date"
                                required
                                value={formData.startDate}
                                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="edit-end-date" style={{ display: 'block', marginBottom: '0.5rem' }}>End Date</label>
                            <input
                                id="edit-end-date"
                                type="date"
                                required
                                value={formData.endDate}
                                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="edit-leave-reason" style={{ display: 'block', marginBottom: '0.5rem' }}>Reason</label>
                        <textarea
                            id="edit-leave-reason"
                            required
                            rows="3"
                            value={formData.reason}
                            onChange={e => setFormData({ ...formData, reason: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        ></textarea>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Balance Adjustment Modal
const AdjustBalanceModal = ({ show, onClose, users, onSuccess, selectedUser, setSelectedUser }) => {
    const [balance, setBalance] = useState({ sick: 0, casual: 0, earned: 0 });
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    // Fetch user balance when selected
    useEffect(() => {
        if (selectedUser) {
            setFetchLoading(true);
            api.get('/leaves/balance', { params: { userId: selectedUser } })
                .then(res => setBalance({ sick: res.data.sick, casual: res.data.casual, earned: res.data.earned }))
                .catch(() => setBalance({ sick: 0, casual: 0, earned: 0 }))
                .finally(() => setFetchLoading(false));
        }
    }, [selectedUser]);

    if (!show) return null;

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.post('/leaves/balance/adjust', { userId: selectedUser, ...balance });
            setMsg('Balance updated!');
            if (onSuccess) onSuccess();
            setTimeout(() => setMsg(null), 2000);
        } catch (e) {
            alert('Failed to update');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '400px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Adjust Leave Balance</h2>
                {msg && <div style={{ color: 'green', marginBottom: '1rem' }}>{msg}</div>}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>Select Employee</label>
                    <select
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        value={selectedUser}
                        onChange={e => setSelectedUser(e.target.value)}
                    >
                        <option value="">-- Select --</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                    </select>
                </div>

                {selectedUser && (
                    fetchLoading ? <div>Loading current balance...</div> : (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div>
                                <label>Sick Leave</label>
                                <input type="number" value={balance.sick} onChange={e => setBalance({ ...balance, sick: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }} />
                            </div>
                            <div>
                                <label>Casual Leave</label>
                                <input type="number" value={balance.casual} onChange={e => setBalance({ ...balance, casual: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }} />
                            </div>
                            <div>
                                <label>Earned Leave</label>
                                <input type="number" value={balance.earned} onChange={e => setBalance({ ...balance, earned: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }} />
                            </div>
                        </div>
                    )
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button onClick={onClose} className="btn btn-secondary">Close</button>
                    {selectedUser && <button onClick={handleSave} disabled={loading} className="btn btn-primary">Save Changes</button>}
                </div>
            </div>
        </div>
    );
};

const LeaveDashboard = () => {
    // ... hooks ...
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('my');
    const [balance, setBalance] = useState(null);
    const [myLeaves, setMyLeaves] = useState([]);
    const [teamLeaves, setTeamLeaves] = useState([]);
    const [allLeaves, setAllLeaves] = useState([]);
    const [users, setUsers] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [allBalances, setAllBalances] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Edit Modal State
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [selectedUser, setSelectedUser] = useState('');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isManager = user.role === 'MANAGER' || user.role === 'ADMIN' || user.role === 'HR';
    const isHR = user.role === 'HR' || user.role === 'ADMIN';

    // ... useEffects & fetch functions remain same ...
    useEffect(() => {
        fetchData();
        if (isManager) fetchTeamData();
        if (isHR) {
            fetchAllLeaves();
            fetchUsersList();
            fetchAllBalances();
        }
    }, [isManager, isHR]);

    const fetchData = async () => {
        try {
            const [balRes, leavesRes, holidaysRes] = await Promise.all([
                api.get('/leaves/balance'),
                api.get('/leaves/my'),
                api.get(`/holidays?year=${new Date().getFullYear()}`)
            ]);
            setBalance(balRes.data);
            setMyLeaves(leavesRes.data);
            setHolidays(holidaysRes.data);
        } catch (error) {
            console.error('Error fetching data', error);
        }
    };

    const fetchTeamData = async () => {
        try {
            const res = await api.get('/leaves/team');
            setTeamLeaves(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchAllLeaves = async () => {
        try {
            const res = await api.get('/leaves/all');
            setAllLeaves(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchUsersList = async () => {
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchAllBalances = async () => {
        try {
            const res = await api.get('/leaves/balances');
            setAllBalances(res.data);
        } catch (error) { console.error(error); }
    };

    // Actions
    const handleCancel = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this request?")) return;
        try {
            await api.delete(`/leaves/${id}`);
            fetchData();
            alert("Leave cancelled.");
        } catch (e) { alert("Failed to cancel."); }
    };

    const handleManagerCancel = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this APPROVED leave? This will refund the leave balance.")) return;
        try {
            await api.patch(`/leaves/${id}/cancel-approved`);
            fetchTeamData();
            if (isHR) fetchAllLeaves();
            alert("Approved leave cancelled and refunded.");
        } catch (e) { alert(e.response?.data?.error || "Failed to cancel."); }
    }

    const handleApproval = async (id, status) => {
        try {
            await api.patch(`/leaves/${id}/status`, { status });
            fetchTeamData();
            if (isHR) fetchAllLeaves();
            alert(`Leave request ${status}`);
        } catch (error) {
            alert('Failed to update status');
        }
    };

    // ... getStatusBadge same ...
    const getStatusBadge = (status) => {
        const colors = {
            PENDING: '#f59e0b',
            APPROVED: '#10b981',
            REJECTED: '#ef4444',
            CANCELLED: '#6b7280'
        };
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 'bold',
                backgroundColor: `${colors[status]}20`, color: colors[status]
            }}>
                {status}
            </span>
        );
    };


    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);

    const handleDownloadReport = async (rangeType, customStart, customEnd) => {
        let start, end;
        const now = new Date();

        if (rangeType === 'last-week') {
            start = new Date(now);
            start.setDate(now.getDate() - 7);
            end = now;
        } else if (rangeType === 'last-month') {
            start = new Date(now);
            start.setMonth(now.getMonth() - 1);
            end = now;
        } else if (rangeType === 'custom') {
            start = new Date(customStart);
            end = new Date(customEnd);
        }

        try {
            const response = await api.get('/leaves/report', {
                params: {
                    startDate: start.toISOString(),
                    endDate: end.toISOString()
                },
                responseType: 'blob'
            });

            // Trigger Download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `leave_report_${start.toISOString().split('T')[0]}_${end.toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setShowExportMenu(false);
        } catch (error) {
            console.error('Download failed', error);
            alert('Failed to download report');
        }
    };

    return (
        <div className="container section">
            {/* Custom Date Range Modal */}
            {showCustomDateModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110 }}>
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '0.5rem', width: '300px' }}>
                        <h3 style={{ marginBottom: '1rem', fontWeight: 'bold' }}>Select Date Range</h3>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            handleDownloadReport('custom', formData.get('start'), formData.get('end'));
                            setShowCustomDateModal(false);
                        }}>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>Start Date</label>
                                <input name="start" type="date" required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.3rem' }} />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>End Date</label>
                                <input name="end" type="date" required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.3rem' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                <button type="button" onClick={() => setShowCustomDateModal(false)} className="btn btn-secondary">Cancel</button>
                                <button type="submit" className="btn btn-primary">Download</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Header ... */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Back to Home
                </button>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* Export Dropdown */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="btn btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <Download size={18} /> Export <ChevronDown size={14} />
                        </button>
                        {showExportMenu && (
                            <div style={{
                                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                                background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minWidth: '160px', zIndex: 50
                            }}>
                                <button
                                    onClick={() => handleDownloadReport('last-week')}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Last Week
                                </button>
                                <button
                                    onClick={() => handleDownloadReport('last-month')}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Last Month
                                </button>
                                <button
                                    onClick={() => { setShowCustomDateModal(true); setShowExportMenu(false); }}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Custom Range...
                                </button>
                            </div>
                        )}
                    </div>
                    {/* This closing parenthesis was likely a typo and is removed */}

                    {isHR && (
                        <button onClick={() => setShowBalanceModal(true)} className="btn btn-secondary">
                            <Settings size={18} style={{ marginRight: '0.5rem' }} /> Manage Balances
                        </button>
                    )}
                    <button onClick={() => setShowApplyModal(true)} className="btn btn-primary">
                        <Plus size={20} style={{ marginRight: '0.5rem' }} /> Apply Leave
                    </button>
                </div>
            </div >

            {/* Tab Navigation */}
            < div style={{ display: 'flex', background: '#f3f4f6', padding: '4px', borderRadius: '0.5rem', width: 'fit-content', marginBottom: '2rem' }}>
                <button onClick={() => setActiveTab('my')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'my' ? 'white' : 'transparent', fontWeight: activeTab === 'my' ? '600' : '500' }}>My Leaves</button>
                {isManager && <button onClick={() => setActiveTab('team')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'team' ? 'white' : 'transparent', fontWeight: activeTab === 'team' ? '600' : '500' }}>Team Requests</button>}
                {isHR && <button onClick={() => setActiveTab('all')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'all' ? 'white' : 'transparent', fontWeight: activeTab === 'all' ? '600' : '500' }}>All Company Leaves</button>}
                {isHR && <button onClick={() => setActiveTab('balances')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'balances' ? 'white' : 'transparent', fontWeight: activeTab === 'balances' ? '600' : '500' }}>Employee Balances</button>}
                <button onClick={() => setActiveTab('calendar')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'calendar' ? 'white' : 'transparent', fontWeight: activeTab === 'calendar' ? '600' : '500' }}>Holiday Calendar</button>
            </div >

            {activeTab === 'my' && (
                <>
                    {/* Stats ... */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                        {[
                            { label: 'Casual Leave', value: balance?.casual || 0, color: '#3b82f6' },
                            { label: 'Sick Leave', value: balance?.sick || 0, color: '#ef4444' },
                            { label: 'Earned Leave', value: balance?.earned || 0, color: '#10b981' },
                        ].map((item, index) => (
                            <div key={index} className="glass-card" style={{ padding: '1.5rem' }}>
                                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{item.label}</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: item.color }}>{item.value}</div>
                                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Days Available</div>
                            </div>
                        ))}
                    </div>

                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Dates</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Reason</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {myLeaves.map(leave => (
                                    <tr key={leave.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontWeight: '500' }}>{leave.type}</td>
                                        <td style={{ padding: '1rem', color: '#4b5563' }}>
                                            {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>({leave.daysCount} days)</div>
                                        </td>
                                        <td style={{ padding: '1rem', color: '#6b7280' }}>{leave.reason}</td>
                                        <td style={{ padding: '1rem' }}>{getStatusBadge(leave.status)}</td>
                                        <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                            {leave.status === 'PENDING' && (
                                                <>
                                                    <button onClick={() => { setSelectedLeave(leave); setShowEditModal(true); }} style={{ color: '#3b82f6', border: 'none', background: 'none', cursor: 'pointer' }} title="Edit Request">
                                                        <Edit size={18} />
                                                    </button>
                                                    <button onClick={() => handleCancel(leave.id)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }} title="Cancel Request">
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {
                (activeTab === 'team' || activeTab === 'all') && (
                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Employee</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Dates</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Reason</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(activeTab === 'team' ? teamLeaves : allLeaves).map(leave => (
                                    <tr key={leave.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontWeight: '500' }}>{leave.user?.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{leave.user?.email}</div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>{leave.type}</td>
                                        <td style={{ padding: '1rem', color: '#4b5563' }}>
                                            {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '1rem', color: '#6b7280' }}>{leave.reason}</td>
                                        <td style={{ padding: '1rem' }}>{getStatusBadge(leave.status)}</td>
                                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                                            {leave.status === 'PENDING' && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                                    <button onClick={() => handleApproval(leave.id, 'APPROVED')} style={{ padding: '0.4rem 0.8rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Approve</button>
                                                    <button onClick={() => handleApproval(leave.id, 'REJECTED')} style={{ padding: '0.4rem 0.8rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Reject</button>
                                                </div>
                                            )}
                                            {leave.status === 'APPROVED' && (
                                                <button onClick={() => handleManagerCancel(leave.id)} style={{ padding: '0.4rem 0.8rem', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {activeTab === 'balances' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="glass-card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                placeholder="Search employee or department..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: '100%', padding: '0.75rem 1rem', paddingLeft: '2.5rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', outline: 'none' }}
                            />
                            <Clock size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        </div>
                    </div>

                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Employee</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Department</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Casual</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Sick</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Earned</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allBalances
                                    .filter(item =>
                                        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        item.department?.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map(item => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '1rem' }}>
                                                <div style={{ fontWeight: '500' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.email}</div>
                                            </td>
                                            <td style={{ padding: '1rem' }}>{item.department || 'N/A'}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.casual || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.sick || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.earned || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => { setSelectedUser(item.id); setShowBalanceModal(true); }}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                                >
                                                    Adjust
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {
                activeTab === 'calendar' && (
                    <HolidayCalendar userId={user.id} showTeamLeaves={activeTab === 'team' || isManager} />
                )
            }

            <ApplyLeaveModal
                show={showApplyModal}
                onClose={() => setShowApplyModal(false)}
                onSuccess={() => { fetchData(); if (isManager) fetchTeamData(); if (isHR) fetchAllLeaves(); }}
                balance={balance}
                holidays={holidays}
            />

            <EditLeaveModal
                show={showEditModal}
                leave={selectedLeave}
                onClose={() => setShowEditModal(false)}
                onSuccess={() => { fetchData(); }}
                holidays={holidays}
            />

            <AdjustBalanceModal
                show={showBalanceModal}
                onClose={() => setShowBalanceModal(false)}
                onSuccess={() => { fetchData(); fetchAllBalances(); }}
                users={users}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
            />
        </div >
    );
};

export default LeaveDashboard;
